/**
 * RestorationResolver (AJIP-16.02.006 · WS9)
 * ---------------------------------------------------------------------
 * Determines the correct action when a valid persisted journey may
 * exist:
 *   NEW_ACTIVATION      — no context, no recoverable record; proceed to activate
 *   RESUME_INMEMORY     — existing in-memory context (WAITING); call engine.resume()
 *   RESTORE_PERSISTED   — recoverable record found; call PersistenceService.restore()
 *   REJECT_ACTIVATION   — active non-restorable context blocks activation
 *   REJECT_RESTORATION  — record cannot be restored (expired, corrupt, ownership)
 *   CONFLICT            — signalled by ConflictDetector separately
 *
 * All persisted restoration flows through:
 *   * PersistenceService.restore()
 *   * PersistenceService.listRecoverable()
 *
 * The Resolver NEVER reads storage directly. The Resolver NEVER writes
 * storage. The Resolver NEVER modifies engine state; it only routes.
 */
import { STATES } from '../core/JourneyState';
import { PersistenceRestorationFailureError } from './ResolutionActivationErrors';

const ACTIONS = Object.freeze({
  NEW_ACTIVATION: 'NEW_ACTIVATION',
  RESUME_INMEMORY: 'RESUME_INMEMORY',
  RESTORE_PERSISTED: 'RESTORE_PERSISTED',
  REJECT_ACTIVATION: 'REJECT_ACTIVATION',
  REJECT_RESTORATION: 'REJECT_RESTORATION',
});

const plan = async ({ engine, persistenceService, request, definition }) => {
  const journeyId = definition && definition.journeyId;
  const principalRef = (request && request.principalContext && request.principalContext.principalRef) || null;
  const hasExplicitResumeFalse = Boolean(
    request && Object.prototype.hasOwnProperty.call(request, 'resumeIntent') && request.resumeIntent === false,
  );

  // In-memory context first — no storage reads needed.
  const ctx = engine && typeof engine.contextOf === 'function' ? engine.contextOf(journeyId) : null;
  if (ctx) {
    if (ctx.currentState === STATES.WAITING) {
      return Object.freeze({ action: ACTIONS.RESUME_INMEMORY, persistenceRecordId: null, principalRef });
    }
    // Any other non-terminal state means an active journey exists; the
    // ConflictDetector will flag this at Resolution level. The Resolver
    // still reports REJECT_ACTIVATION here so downstream logic is
    // explicit about the intent.
    if (ctx.currentState !== STATES.NOT_STARTED
        && ctx.currentState !== STATES.COMPLETED
        && ctx.currentState !== STATES.CANCELLED
        && ctx.currentState !== STATES.FAILED) {
      return Object.freeze({ action: ACTIONS.REJECT_ACTIVATION, persistenceRecordId: null, principalRef, reason: `EXISTING_STATE_${ctx.currentState}` });
    }
  }

  // Persisted record — only look when persistence is enabled and
  // resumeIntent is either explicit or omitted (default = new activation).
  if (persistenceService && persistenceService.enabled) {
    try {
      const recoverable = await persistenceService.listRecoverable({ principalRef });
      const match = (recoverable || []).find((r) => r.journeyDefinitionId === journeyId);
      if (match) {
        if (hasExplicitResumeFalse && request.activationIntent === true) {
          // Caller explicitly opted out of restoration despite a
          // recoverable record. Honour that intent — a downstream
          // ConflictDetector rule may still block it.
          return Object.freeze({ action: ACTIONS.NEW_ACTIVATION, persistenceRecordId: null, principalRef });
        }
        return Object.freeze({
          action: ACTIONS.RESTORE_PERSISTED,
          persistenceRecordId: match.persistenceRecordId,
          principalRef,
        });
      }
    } catch (_e) {
      // Framework-level listRecoverable must never throw. If it does,
      // treat the absence as new activation.
    }
  }

  return Object.freeze({ action: ACTIONS.NEW_ACTIVATION, persistenceRecordId: null, principalRef });
};

/**
 * Execute the plan. Returns a discriminated envelope; caller (Activation
 * Service) uses this to build the ActivationResult.
 */
const execute = async ({ engine, persistenceService, planResult, journeyId }) => {
  if (!planResult || !planResult.action) {
    throw new PersistenceRestorationFailureError('RestorationResolver.execute requires a plan');
  }
  switch (planResult.action) {
    case ACTIONS.NEW_ACTIVATION: {
      const ctx = engine.activate(journeyId);
      return Object.freeze({ kind: ACTIONS.NEW_ACTIVATION, currentState: ctx.currentState });
    }
    case ACTIONS.RESUME_INMEMORY: {
      const ctx = engine.resume(journeyId);
      return Object.freeze({ kind: ACTIONS.RESUME_INMEMORY, currentState: ctx.currentState });
    }
    case ACTIONS.RESTORE_PERSISTED: {
      if (!persistenceService || !persistenceService.enabled) {
        throw new PersistenceRestorationFailureError('Persistence disabled — cannot restore');
      }
      try {
        const outcome = await persistenceService.restore({
          persistenceRecordId: planResult.persistenceRecordId,
          engine,
          principalRef: planResult.principalRef,
        });
        return Object.freeze({
          kind: ACTIONS.RESTORE_PERSISTED,
          currentState: outcome.context.currentState,
          persistenceRecordId: outcome.record.persistenceRecordId,
        });
      } catch (e) {
        throw new PersistenceRestorationFailureError(
          `Restoration failed: ${e.message}`,
          { originalErrorName: e.name },
        );
      }
    }
    case ACTIONS.REJECT_ACTIVATION:
    case ACTIONS.REJECT_RESTORATION: {
      return Object.freeze({ kind: planResult.action, reason: planResult.reason || 'REJECTED' });
    }
    default:
      throw new PersistenceRestorationFailureError(`Unknown plan action '${planResult.action}'`);
  }
};

export { ACTIONS, plan, execute };
